import { defineStore } from "pinia";
import { computed, onMounted, ref, watch } from "vue";
import { Organizations, DataInstance } from "../platform-data";
import { collection, onSnapshot, QuerySnapshot } from "firebase/firestore";
import { firestore } from "@/util/firebase";


export const useOrganizations = defineStore('organizations', () => {
  const _organizations = ref<Record<string, DataInstance<Organizations.Model>>>({});
  const organizations = computed(() => _organizations.value);

  const waitingResolves: (() => void)[] = [];

  const searchQuery = ref("");

  const orgList = computed(() => Object.values(organizations.value));

  const isReady = ref(false);

  const HQCountryOptions = computed(() => {
    const countrySet = new Set<string>();

    Object.values(organizations.value).forEach(org => {
      if (org["HQ Country"]) {
        countrySet.add(org["HQ Country"]);
      }
    });
    return Array.from(countrySet);
  });

  const HQStateOptions = computed(() => {
    const stateSet = new Set<string>();

    Object.values(organizations.value).forEach(org => {
      if (org["HQ State Or Province"]) {
        stateSet.add(org["HQ State Or Province"]);
      }
    });
    
    return Array.from(stateSet);
  });

  const filteredOrganizations = computed(() => {
    const query = searchQuery.value.trim();

    if (!query) return orgList.value;
  
    const filtered = orgList.value.filter((org) => {
    
      const match = fuzzyMatch(org["Organization Name"], query);
    
      return match;
    });
    return filtered;
  });

  function fuzzyMatch(source: string, query: string): boolean {
    if (!source || !query) return false;
  
    const loweredSource = source.trim().toLowerCase();
    const loweredQuery = query.trim().toLowerCase();  
  
    const includesQuery = loweredSource.includes(loweredQuery);
  
    return includesQuery;
  }

  function setupListeners() {
    const orgCollection = collection(firestore, "organizations");

    onSnapshot(orgCollection, (snapshot: QuerySnapshot) => {
      const updatedOrganizations: Record<string, DataInstance<Organizations.Model>> = {};

      snapshot.forEach(doc => {
        updatedOrganizations[doc.id] = {
          $id: doc.id,
          ...(doc.data() as Organizations.Model),
        };
      });

      _organizations.value = updatedOrganizations;

      isReady.value = true;

      // Resolve all waiting promises
      while (waitingResolves.length > 0) {
        const resolve = waitingResolves.shift();
        if (resolve) resolve();
      }

    }, (error) => {
      console.error("Error fetching organizations:", error);
      isReady.value = true;

      // Resolve all waiting promises in case of error
      while (waitingResolves.length > 0) {
        const resolve = waitingResolves.shift();
        if (resolve) resolve();
      }
    });
  }

  const waitForReady = async () => {
    if (isReady.value) {
      console.log("Organizations ready!");
      return true;
    } else {
      return new Promise<void>(resolve => waitingResolves.push(resolve));
    }
  };

  onMounted(async () => {
    await waitForReady();
  });

  setupListeners();

  return {
    organizations, isReady, HQCountryOptions, HQStateOptions, waitForReady, searchQuery, filteredOrganizations
  };
});
